import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { RecordTypeStatusFragment } from 'types/graphql'

import { RecordTypeStatusDisplay } from 'src/components/RecordTypeStatus/RecordTypeStatusDisplay'
import { useRecordTypeStatusesQuery } from 'src/fetch/recordTypes'

type Props = {
  currentStatus?: RecordTypeStatusFragment | null | undefined
  disabledRecordTypeStatusId?: number | null
  isRequired?: boolean
  label?: string
  onValueChange: (value: number | undefined) => void
  recordTypeId: number | null | undefined
  value: number | undefined | null
  errorMessage?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}
const RecordTypeStatusSelect = ({
  currentStatus,
  disabledRecordTypeStatusId,
  errorMessage,
  isRequired,
  label,
  onBlur,
  onValueChange,
  recordTypeId,
  value,
}: Props) => {
  const { data, isLoading } = useRecordTypeStatusesQuery({ recordTypeId })

  const baseStatuses = useMemo(() => {
    return data?.recordType?.statuses ?? []
  }, [data?.recordType?.statuses])

  const isCurrentStatusArchived =
    !!currentStatus && !baseStatuses.some((s) => s.id === currentStatus.id)

  const statuses = useMemo(() => {
    return [...baseStatuses, ...(isCurrentStatusArchived ? [currentStatus] : [])]
  }, [baseStatuses, currentStatus, isCurrentStatusArchived])

  const options: SelectOption<number>[] = useMemo(() => {
    return statuses.map((s) => ({
      label: s.name || '',
      value: s.id,
      customDisplay: () => (
        <RecordTypeStatusDisplay
          status={s}
          isArchived={s.id === currentStatus?.id && isCurrentStatusArchived}
        />
      ),
      disabled: s.id === disabledRecordTypeStatusId,
    }))
  }, [currentStatus?.id, disabledRecordTypeStatusId, isCurrentStatusArchived, statuses])

  const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<number> | undefined) => {
      onValueChange(option?.value)
    },
    [onValueChange]
  )

  return (
    <Select
      getOptionKey={(o) => o.value}
      isLoading={isLoading}
      isRequired={isRequired}
      label={label}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder={isLoading ? 'Loading…' : 'Select status'}
      selectedOption={selectedOption}
      errorMessage={errorMessage}
      isInvalid={!!errorMessage}
      onBlur={onBlur}
      shouldShowSelectedOptionCustomDisplay
    />
  )
}

export default RecordTypeStatusSelect
